import React, { useEffect, useRef, useState } from "react";
import { InboxOutlined } from "@ant-design/icons";
import { Avatar, Form, Input, message, Modal, Select, Upload, type UploadFile } from "antd";

import { teamActions } from "@fitness-app/app-store";
import { type SupportRequestAttachment } from "@fitness-app/data-models";

import { useTagOptions } from "~/hooks/useTagOptions";
import { useAppDispatch } from "~/store/initializeStore";
import { useCreateSupportRequest } from "../hooks/useSupportApi";
import { useSupportStaff } from "../hooks/useSupportStaff";
import { useTraineeSearch } from "../hooks/useTraineeSearch";
import { uploadSupportAttachment } from "../supportAttachments";

// Prefill for entry points outside the board: the chat message dropdown and the
// trainee profile menu. `metadata` keeps the relation to the source (chat message).
export interface SupportRequestPrefill {
  title?: string;
  description?: string;
  clientEmail?: string;
  trainee?: { id: string; label: string };
  metadata?: { chatMessageId?: string; chatChannelId?: string };
}

interface SupportRequestFormModalProps {
  open: boolean;
  onClose: () => void;
  prefill?: SupportRequestPrefill;
}

interface FormValues {
  title: string;
  description?: string;
  traineeId?: string;
  clientEmail?: string;
  assigneeUserId?: string;
  tags?: string[];
}

export const SupportRequestFormModal: React.FC<SupportRequestFormModalProps> = ({ open, onClose, prefill }) => {
  const [form] = Form.useForm<FormValues>();
  const dispatch = useAppDispatch();
  const {
    options: traineeOptions,
    isSearching,
    setSearchValue,
  } = useTraineeSearch({
    enabled: open,
    extraOption: prefill?.trainee
      ? { value: prefill.trainee.id, label: prefill.trainee.label, metadata: { email: prefill.clientEmail } }
      : null,
  });
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const uploadedByUid = useRef<Record<string, SupportRequestAttachment>>({});
  const createRequest = useCreateSupportRequest();
  const { staff } = useSupportStaff();
  const { options: tagOptions } = useTagOptions("supportRequestTags");

  // Entry points outside the board (chat, trainee profile) may not have the
  // trainer config (tag options) in the store yet — fetch once per open, not on
  // every debounced trainee search.
  useEffect(() => {
    if (open) {
      void dispatch(teamActions.fetchTrainerConfig());
    }
  }, [dispatch, open]);

  useEffect(() => {
    if (open && prefill) {
      form.setFieldsValue({
        title: prefill.title,
        description: prefill.description,
        clientEmail: prefill.clientEmail,
        traineeId: prefill.trainee?.id,
      });
    }
  }, [open, prefill, form]);

  const resetForm = () => {
    form.resetFields();
    setFileList([]);
    uploadedByUid.current = {};
  };

  const handleSubmit = (values: FormValues) => {
    const attachments = fileList
      .map((file) => uploadedByUid.current[file.uid])
      .filter((attachment): attachment is SupportRequestAttachment => Boolean(attachment));

    createRequest.mutate(
      {
        title: values.title,
        description: values.description || undefined,
        traineeId: values.traineeId || undefined,
        clientEmail: values.clientEmail || undefined,
        assigneeUserId: values.assigneeUserId || undefined,
        tags: values.tags?.length ? values.tags : undefined,
        attachments: attachments.length ? attachments : undefined,
        metadata: prefill?.metadata,
      },
      {
        onSuccess: () => {
          resetForm();
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      title="Nowe zgłoszenie"
      open={open}
      onCancel={() => {
        resetForm();
        onClose();
      }}
      onOk={() => form.submit()}
      okText="Dodaj zgłoszenie"
      cancelText="Anuluj"
      confirmLoading={createRequest.isLoading}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} className="pt-2">
        <Form.Item name="title" label="Tytuł" rules={[{ required: true, message: "Podaj tytuł zgłoszenia", min: 3 }]}>
          <Input placeholder="np. Klientka nie może zmienić karty do płatności" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Opis"
          tooltip="Co się dzieje, u kogo, od kiedy — im więcej szczegółów, tym szybsza diagnoza."
        >
          <Input.TextArea rows={5} placeholder="Opis problemu, kroki, oczekiwane zachowanie..." />
        </Form.Item>

        <Form.Item name="traineeId" label="Klient (opcjonalnie)">
          <Select
            showSearch
            allowClear
            filterOption={false}
            loading={isSearching}
            placeholder="Szukaj po imieniu, nazwisku lub emailu"
            onSearch={(value) => setSearchValue(value)}
            onSelect={(_traineeId: string, option: { metadata?: { email?: string } }) => {
              if (option?.metadata?.email && !form.getFieldValue("clientEmail")) {
                form.setFieldValue("clientEmail", option.metadata.email);
              }
            }}
            options={traineeOptions}
            notFoundContent="Brak wyników wyszukiwania"
          />
        </Form.Item>

        <Form.Item
          name="clientEmail"
          label="Email klienta (opcjonalnie)"
          rules={[{ type: "email", message: "Niepoprawny adres email" }]}
        >
          <Input placeholder="Jeśli klient nie jest jeszcze w systemie" />
        </Form.Item>

        <Form.Item name="tags" label="Tagi (opcjonalnie)">
          <Select
            mode="multiple"
            allowClear
            placeholder="Wybierz tagi zgłoszenia"
            options={tagOptions}
            notFoundContent="Brak zdefiniowanych tagów"
          />
        </Form.Item>

        <Form.Item name="assigneeUserId" label="Przypisany (opcjonalnie)">
          <Select
            allowClear
            showSearch
            optionFilterProp="name"
            placeholder="Nieprzypisane"
            options={staff.map((member) => ({
              value: member.userId,
              name: member.name,
              label: (
                <span className="flex items-center gap-2">
                  <Avatar size={18} src={member.avatarUrl || undefined}>
                    {member.name.charAt(0)}
                  </Avatar>
                  {member.name}
                </span>
              ),
            }))}
          />
        </Form.Item>

        <Form.Item label="Załączniki (opcjonalnie)">
          <Upload.Dragger
            multiple
            fileList={fileList}
            onChange={({ fileList: nextFileList }) => setFileList(nextFileList)}
            customRequest={async ({ file, onSuccess, onError }) => {
              const uploadFile = file as File & { uid: string };

              try {
                const attachment = await uploadSupportAttachment(uploadFile);
                uploadedByUid.current[uploadFile.uid] = attachment;
                onSuccess?.(attachment.url);
              } catch (error) {
                message.error(error instanceof Error ? error.message : "Błąd wysyłania pliku");
                onError?.(error as Error);
              }
            }}
            onRemove={(file) => {
              delete uploadedByUid.current[file.uid];
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Kliknij albo przeciągnij pliki (np. screenshoty)</p>
          </Upload.Dragger>
        </Form.Item>
      </Form>
    </Modal>
  );
};
